<!--
  The page shell both Lucent SPAs render their routes inside.

  Sticky header (brand lockup + subtitle chip + nav + theme toggle + app
  actions), a flexible `<main>`, and a footer bar. Extracted from the two
  `App.vue` files, which had the same structure and differed only in content:
  the dashboard's subtitle reads `organisation: <client>` where the admin's
  reads `Admin · Operator`, the nav links differ, and the footers carry
  different status information.

  **Theme is owned here, not by the caller.** Both apps did nothing with the
  theme beyond wiring the toggle, so `useTheme()` is installed internally and
  neither app has to know the mechanism. If a caller ever needs the value, add
  a slot prop rather than pushing the state back out.

  ## Slots

    brand-sub        Subtitle chip under the wordmark. Wears `.lucent-realm`.
    nav              Primary nav links. Wears `.lucent-nav`.
    header-actions   App-specific actions, rendered *after* the theme toggle
                     (user chip, sign out).
    default          Page content.
    footer           The whole footer bar's contents. The `.lucent-footer` /
                     `.lucent-footer-inner` wrappers are provided.

  ## Two things it does NOT do, deliberately

  - **No mobile menu toggle button.** `chrome.css` still carries the
    `[data-open="true"]` rules and the `.lucent-menu-toggle` styling, and the
    `menuOpen` prop still drives the attribute — but neither app renders the
    button today (both had it commented out, the dashboard because it was down
    to a single nav link). Rendering one here would put a hamburger on mobile
    where there currently is none. The plumbing is intact for whichever app
    wants it back first.
  - **No route awareness.** It reads no route and performs no navigation
    decisions; `chromeless` is passed in by the caller, which is the only one
    that knows whether the current route is the bespoke login layout.
    `RouterLink` is used for the home lockup only — an `<a href>` there would
    full-page-reload the SPA.
-->

<script setup lang="ts">
import { RouterLink } from "vue-router";

import { useTheme } from "../composables/useTheme.ts";
import BrandMark from "./BrandMark.vue";
import ThemeToggle from "./ThemeToggle.vue";

withDefaults(
  defineProps<{
    /**
     * Suppress header and footer and render the default slot bare.
     *
     * For the login routes, which are full-bleed bespoke layouts that claim
     * the viewport. Both apps have one.
     */
    chromeless?: boolean;
    /**
     * `centred` wraps the default slot in `.lucent-shell` — a max-width
     * column with page gutters. Used by insights-admin, where every view
     * wants the same measure.
     *
     * `bleed` emits the slot directly into `<main>`, leaving width to the
     * routed page. Used by dashboard-app, where `InsightsPageBody` decides
     * per-view whether to span the viewport.
     */
    main?: "centred" | "bleed";
    /** Wordmark text. */
    brandLabel?: string;
    /** Route the lockup links to. */
    homeTo?: string;
    /** Accessible name for the lockup link, e.g. "LucentLake Admin home". */
    lockupLabel?: string;
    /** Drives `data-open` on the header, which `chrome.css`'s mobile rules read. */
    menuOpen?: boolean;
  }>(),
  {
    chromeless: false,
    main: "centred",
    brandLabel: "LucentLake",
    homeTo: "/",
    lockupLabel: "LucentLake home",
    menuOpen: false,
  },
);

const { theme, toggleTheme } = useTheme();
</script>

<template>
  <div class="lucent-page">
    <header
      v-if="!chromeless"
      class="lucent-header"
      :data-open="menuOpen ? 'true' : 'false'"
    >
      <div class="lucent-header-inner">
        <RouterLink :to="homeTo" class="lucent-lockup" :aria-label="lockupLabel">
          <BrandMark />
          <span>
            <span class="lucent-brand">{{ brandLabel }}</span>
            <span class="lucent-realm"><slot name="brand-sub" /></span>
          </span>
        </RouterLink>

        <nav class="lucent-nav" aria-label="Primary">
          <slot name="nav" />
        </nav>

        <div class="lucent-header-actions">
          <ThemeToggle :theme="theme" @toggle="toggleTheme" />
          <slot name="header-actions" />
        </div>
      </div>
    </header>

    <main class="lucent-main">
      <div v-if="!chromeless && main === 'centred'" class="lucent-shell">
        <slot />
      </div>
      <slot v-else />
    </main>

    <footer v-if="!chromeless" class="lucent-footer">
      <div class="lucent-footer-inner">
        <slot name="footer" />
      </div>
    </footer>
  </div>
</template>
